import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { peso } from "../lib/api";

const V = {
  primary: "bg-[#5C4033] text-white hover:bg-[#4A3326]",
  terra: "bg-[#C85A32] text-white hover:bg-[#B04B26]",
  green: "bg-[#2D6A4F] text-white hover:bg-[#23533E]",
  outline: "bg-white text-[#5C4033] border-2 border-[#5C4033]/40 hover:border-[#5C4033] hover:bg-[#F3EDE2]",
  danger: "bg-white text-[#B91C1C] border-2 border-[#B91C1C]/40 hover:bg-[#FEF2F2]",
  ghost: "text-[#5C4033] hover:bg-[#F3EDE2]",
};

export const Btn = ({ v = "primary", sm, loading, className = "", children, ...p }) => (
  <button
    {...p}
    disabled={p.disabled || loading}
    className={`inline-flex items-center justify-center gap-2 rounded-xl font-bold tracking-wide transition-[background-color,border-color,transform] duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${sm ? "h-10 px-3 text-sm" : "h-12 sm:h-14 px-5 text-base"} ${V[v]} ${className}`}
  >
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
    {children}
  </button>
);

export const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-base font-semibold text-[#4A3326] mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-sm text-[#6E6259] mt-1">{hint}</span>}
  </label>
);

const inputCls = "w-full h-12 sm:h-14 rounded-xl border-2 border-[#5C4033]/25 bg-white px-4 text-lg text-[#2F2A24] focus:outline-none focus:border-[#C85A32] focus:ring-2 focus:ring-[#C85A32]/20 disabled:bg-[#F3EDE2]";
export const Input = (p) => <input {...p} className={`${inputCls} ${p.className || ""}`} />;
export const TextArea = (p) => <textarea {...p} className={`${inputCls} h-24 py-3 ${p.className || ""}`} />;
export const Select = ({ children, ...p }) => (
  <select {...p} className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2220%22 height=%2220%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%235C4033%22 stroke-width=%222.5%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-no-repeat bg-[right_14px_center] pr-10 ${p.className || ""}`}>
    {children}
  </select>
);

const SC = {
  draft: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]", submitted: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  pending_verification: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", pending: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  info_requested: "bg-[#FFEDD5] text-[#9A3412] border-[#FED7AA]", verified: "bg-[#E8F5E9] text-[#1B5E3F] border-[#B7DFC4]",
  finalized: "bg-[#1B5E3F] text-white border-[#1B5E3F]", approved: "bg-[#E8F5E9] text-[#1B5E3F] border-[#B7DFC4]",
  paid: "bg-[#2D6A4F] text-white border-[#2D6A4F]", rejected: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
  cancelled: "bg-[#F3F4F6] text-[#4B5563] border-[#E5E7EB]", reported: "bg-[#2D6A4F] text-white border-[#2D6A4F]",
  no_collection: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", service_canceled: "bg-[#E7E2DC] text-[#4B4038] border-[#D6CCC2]",
  not_yet_reported: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]", local: "bg-[#F3E8FF] text-[#6B21A8] border-[#E9D5FF]",
};

export const Badge = ({ s, testid }) => {
  const { t } = useI18n();
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider whitespace-nowrap ${SC[s] || SC.draft}`}>
      {s === "local" ? t("saved_locally") : t(`status_${s}`)}
    </span>
  );
};

export const Money = ({ c, className = "", testid }) => (
  <span data-testid={testid} className={`font-mono tabular-nums ${c < 0 ? "text-[#B91C1C]" : ""} ${className}`}>{peso(c)}</span>
);

export const Card = ({ className = "", children, ...p }) => (
  <div {...p} className={`rounded-2xl border border-[#E4DACD] bg-white p-5 sm:p-6 shadow-[0_1px_0_rgba(92,64,51,0.04)] ${className}`}>{children}</div>
);

export const PageHeader = ({ title, subtitle, back = "/", right }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 fade-up">
    <div>
      {back && (
        <Link to={back} data-testid="page-back-link" className="inline-flex items-center gap-1 text-sm font-semibold text-[#C85A32] mb-2 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      )}
      <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2F2A24] tracking-tight">{title}</h1>
      {subtitle && <p className="text-[#6E6259] mt-1 text-base">{subtitle}</p>}
    </div>
    {right}
  </div>
);

export const Empty = ({ children }) => (
  <div className="rounded-2xl border-2 border-dashed border-[#E4DACD] p-8 text-center text-[#6E6259]">{children}</div>
);

export const Th = ({ children, r }) => <th className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-[#6E6259] bg-[#F3EDE2] ${r ? "text-right" : "text-left"}`}>{children}</th>;
export const Td = ({ children, r, className = "" }) => <td className={`px-3 py-2.5 border-t border-[#EFE7DB] align-top ${r ? "text-right" : ""} ${className}`}>{children}</td>;

export const Stat = ({ label, c, value, tone = "", testid, note }) => (
  <div data-testid={testid} className={`rounded-2xl border border-[#E4DACD] bg-white p-4 ${tone}`}>
    <div className="text-xs font-bold uppercase tracking-wider text-[#6E6259]">{label}</div>
    <div className="mt-1 text-xl sm:text-2xl font-bold text-[#2F2A24]">{value !== undefined ? value : <Money c={c} />}</div>
    {note && <div className="text-xs text-[#6E6259] mt-1">{note}</div>}
  </div>
);

export const useNames = (meta) => {
  const g = Object.fromEntries((meta?.gkks || []).map((x) => [x.id, x.name]));
  const u = Object.fromEntries((meta?.units || []).map((x) => [x.id, x.name]));
  g.PARISH = "Parish Central Fund";
  return { gName: (id) => g[id] || "—", uName: (id) => (id ? u[id] || "—" : "GKK-level") };
};
